import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
// eslint-disable-next-line no-unused-vars
import { satsueiDataTorokuService } from '../../services/satsueiDataTorokuService' // Will be used when backend is ready
import { useApiError } from '../../../shared/hooks/useApiError'
import MessageDialog from '../../../shared/ui/MessageDialog'
import Button from '../../../shared/ui/Button'

const CameraSettingsModal = ({ isOpen, onClose, onCameraListUpdate }) => {
  const [cameras, setCameras] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showRegisterForm, setShowRegisterForm] = useState(false)
  const [showKeyForm, setShowKeyForm] = useState(false)
  const [formData, setFormData] = useState({
    cameraName: '',
    cameraKey: '',
    connectionUrl: ''
  })

  const {
    showErrorDialog,
    errorMessage,
    messageType,
    handleApiError,
    handleApiResponse,
    closeErrorDialog,
    showSuccessMessage
  } = useApiError()

  useEffect(() => {
    if (isOpen) {
      loadCameraList()
    }
  }, [isOpen])

  const loadCameraList = async () => {
    try {
      setIsLoading(true)
      
      // PLACEHOLDER: Use mock data for now since backend is not ready
      // TODO: Uncomment below and remove placeholder code when backend is ready
      /*
      const response = await satsueiDataTorokuService.getCameraList()
      
      if (handleApiResponse(response, 'カメラリストの取得に失敗しました')) {
        return
      }

      if (response.cameraList) {
        setCameras(response.cameraList)
      } else {
        setCameras([])
      }
      */
      
      // Mock data - load from localStorage or use default
      const savedCameras = localStorage.getItem('cameraList')
      if (savedCameras) {
        setCameras(JSON.parse(savedCameras))
      } else {
        // Default placeholder cameras
        const defaultCameras = [
          { cameraId: '1', cameraName: 'カメラ1', cameraKey: 'key1', connectionUrl: 'https://stg-mic-cam.gkun.net/MIC-CAM-A01001/video_feed' },
          { cameraId: '2', cameraName: 'カメラ2', cameraKey: 'key2', connectionUrl: 'http://192.168.1.101' }
        ]
        setCameras(defaultCameras)
        localStorage.setItem('cameraList', JSON.stringify(defaultCameras))
      }
    } catch (error) {
      // Use default cameras on error
      const defaultCameras = [
        { cameraId: '1', cameraName: 'カメラ1', cameraKey: 'key1', connectionUrl: 'http://192.168.1.100' },
        { cameraId: '2', cameraName: 'カメラ2', cameraKey: 'key2', connectionUrl: 'http://192.168.1.101' }
      ]
      setCameras(defaultCameras)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = (cameraId) => {
    if (!confirm('このカメラを削除しますか？')) {
      return
    }

    // Remove from cameras list
    setCameras(prev => prev.filter(camera => camera.cameraId !== cameraId))
  }

  const handleRegister = () => {
    if (!formData.cameraName || !formData.cameraKey) {
      handleApiResponse({ returnCode: '400', message: 'カメラ名とカメラキーを入力してください' }, '入力エラー')
      return
    }

    // Add to cameras list (will be saved when "保存" button is clicked)
    const newCamera = {
      cameraId: `new-${Date.now()}`, // Temporary ID for new cameras
      cameraName: formData.cameraName,
      cameraKey: formData.cameraKey,
      connectionUrl: formData.connectionUrl || ''
    }
    
    setCameras(prev => [...prev, newCamera])
    setShowRegisterForm(false)
    setFormData({ cameraName: '', cameraKey: '', connectionUrl: '' })
  }

  const handleRegisterWithKey = () => {
    if (!formData.cameraKey) {
      handleApiResponse({ returnCode: '400', message: 'カメラキーを入力してください' }, '入力エラー')
      return
    }

    // Add to cameras list (will be saved when "保存" button is clicked)
    const newCamera = {
      cameraId: `new-${Date.now()}`, // Temporary ID for new cameras
      cameraName: `カメラ-${formData.cameraKey}`, // Auto-generate name from key
      cameraKey: formData.cameraKey,
      connectionUrl: formData.connectionUrl || ''
    }
    
    setCameras(prev => [...prev, newCamera])
    setShowKeyForm(false)
    setFormData({ cameraName: '', cameraKey: '', connectionUrl: '' })
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)

      // PLACEHOLDER: Use localStorage for now since backend is not ready
      // TODO: Uncomment below and remove placeholder code when backend is ready
      /*
      const response = await satsueiDataTorokuService.saveCameraList(cameras)
      
      if (handleApiResponse(response, 'カメラの保存に失敗しました')) {
        return
      }

      showSuccessMessage('設定を保存しました')
      await loadCameraList()
      if (onCameraListUpdate) {
        onCameraListUpdate()
      }
      */
      
      // Mock save - update localStorage
      localStorage.setItem('cameraList', JSON.stringify(cameras))
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500))

      showSuccessMessage('設定を保存しました')
      await loadCameraList()
      if (onCameraListUpdate) {
        onCameraListUpdate()
      }
    } catch (error) {
      handleApiError(error, 'カメラの保存に失敗しました')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    setShowRegisterForm(false)
    setShowKeyForm(false)
    setFormData({ cameraName: '', cameraKey: '', connectionUrl: '' })
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="camera-settings-modal">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">カメラの設定</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-modal-button"
            >
              <X size={20} />
            </button>
          </div>

          <div className="mb-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">読み込み中...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Camera List */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">設定済カメラ</h4>
                  {cameras.length === 0 ? (
                    <p className="text-sm text-gray-500">登録されているカメラがありません</p>
                  ) : (
                    <div className="space-y-2">
                      {cameras.map((camera, index) => (
                        <div
                          key={camera.cameraId || index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200"
                        >
                          <span className="text-sm text-gray-700">
                            設定済カメラ: {index + 1}台目→{camera.cameraName || '名称あり'}
                          </span>
                          <button
                            onClick={() => handleDelete(camera.cameraId)}
                            className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            削除
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Register Form */}
                {showRegisterForm && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">新規登録</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          カメラ名
                        </label>
                        <input
                          type="text"
                          value={formData.cameraName}
                          onChange={(e) => setFormData({ ...formData, cameraName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="カメラ名を入力"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          カメラキー/ID
                        </label>
                        <input
                          type="text"
                          value={formData.cameraKey}
                          onChange={(e) => setFormData({ ...formData, cameraKey: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="カメラキーを入力"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          接続URL/IP
                        </label>
                        <input
                          type="text"
                          value={formData.connectionUrl}
                          onChange={(e) => setFormData({ ...formData, connectionUrl: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="接続URLまたはIPアドレス"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          onClick={handleRegister}
                          disabled={isLoading}
                          className="flex-1"
                        >
                          登録
                        </Button>
                        <Button
                          onClick={() => {
                            setShowRegisterForm(false)
                            setFormData({ cameraName: '', cameraKey: '', connectionUrl: '' })
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          キャンセル
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Key Form */}
                {showKeyForm && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">カメラを特定のキー</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          カメラキー
                        </label>
                        <input
                          type="text"
                          value={formData.cameraKey}
                          onChange={(e) => setFormData({ ...formData, cameraKey: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="カメラキーを入力"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          接続URL/IP
                        </label>
                        <input
                          type="text"
                          value={formData.connectionUrl}
                          onChange={(e) => setFormData({ ...formData, connectionUrl: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="接続URLまたはIPアドレス"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          onClick={handleRegisterWithKey}
                          disabled={isLoading}
                          className="flex-1"
                        >
                          登録
                        </Button>
                        <Button
                          onClick={() => {
                            setShowKeyForm(false)
                            setFormData({ cameraName: '', cameraKey: '', connectionUrl: '' })
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          キャンセル
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {!showRegisterForm && !showKeyForm && (
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => {
                        if (cameras.length >= 4) {
                          handleApiResponse({ returnCode: '400', message: 'カメラは4台まで登録できます' }, 'エラー')
                          return
                        }
                        
                        setShowRegisterForm(true)
                        setShowKeyForm(false)
                      }}
                      className="flex-1"
                    >
                      新規登録
                    </Button>
                    <Button
                      onClick={() => {
                        if (cameras.length >= 4) {
                          handleApiResponse({ returnCode: '400', message: 'カメラは4台まで登録できます' }, 'エラー')
                          return
                        }
                        
                        setShowKeyForm(true)
                        setShowRegisterForm(false)
                      }}
                      className="flex-1"
                    >
                      カメラを特定のキー
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-2">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              loading={isSaving}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2"
            >
              {isSaving ? '保存中...' : '保存'}
            </Button>
            {/* <Button
              onClick={handleClose}
              variant="outline"
              className="px-4 py-2"
            >
              閉じる
            </Button> */}
          </div>
        </div>
      </div>

      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title={
          messageType === 'error' ? 'エラー' :
            messageType === 'success' ? '成功' :
              messageType === 'warning' ? '警告' :
                '情報'
        }
        message={errorMessage}
        type={messageType}
        buttonText="閉じる"
        data-testid="message-dialog"
      />
    </>
  )
}

export default CameraSettingsModal

